import { FilteredCreditApplication, StepStateUpdater } from '@applyonline/shared'
import { DateOnly, format } from '@if/core/date'
import { EPaymentPlan } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import useTermPaymentsTable from '../../ViewCreditApplication/InitialApprobationPage/hooks'
import { WorksheetForm } from '../schema-hooks'
import PlanCard from './PlanCard'
import { useEffect, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface Props {
  soonestDeliveryOn: Date
  creditApp: FilteredCreditApplication
  promoTerm?: number
  codePromo?: string | null
  setStepState: StepStateUpdater
  setNext: () => void
  setPrevious: () => void
}

const PlanSelectionStep = ({
  soonestDeliveryOn,
  creditApp,
  promoTerm,
  codePromo,
  setStepState,
  setNext,
  setPrevious,
}: Props) => {
  const { t } = useTranslation()
  const { control, setValue, getValues } = useFormContext<WorksheetForm>()

  const {
    field: { value: term, onChange: onTermChange, onBlur: onTermBlur },
  } = useController({ name: 'term', control })
  const finalAmount = useWatch({ control, name: 'amountRequested' })
  const firstPaymentOn = useWatch({ control, name: 'firstPaymentOn' })

  const [monthlyPaymentPerMonths, isLoadingTerms] = useTermPaymentsTable(creditApp, {
    firstPaymentOn,
    activationDate: soonestDeliveryOn,
    requestedAmount: Number(finalAmount),
    paymentPlanId: EPaymentPlan.RegularDailyInterests,
  })

  // Shortest term first, matching the card layout (24 → 36 → 48).
  const plans = useMemo(
    () => [...monthlyPaymentPerMonths].sort((a, b) => a.term - b.term),
    [monthlyPaymentPerMonths],
  )

  // Smallest monthly payment wins the badge; strict "<" resolves ties to the shortest term.
  const smallestPaymentTerm = useMemo(() => {
    if (plans.length < 2) return null
    return plans.reduce((best, plan) => (plan.amountWithoutInsurance < best.amountWithoutInsurance ? plan : best))
      .term
  }, [plans])

  if (codePromo) {
    if (promoTerm && monthlyPaymentPerMonths.find((to) => to.term === promoTerm)) {
      setNext()
    }
  }
  useEffect(() => setStepState({ loading: isLoadingTerms }), [isLoadingTerms, setStepState])

  const showPromoCodeInvalidMessage =
    codePromo && (!promoTerm || !monthlyPaymentPerMonths.find((to) => to.term === promoTerm))

  const handleSelectPlan = (plan: (typeof plans)[number]) => {
    onTermChange(plan.term)
    onTermBlur()
  }

  const selectedPlan = plans.find((mp) => mp.term === term)

  const handleConfirmPlan = () => {
    if (showPromoCodeInvalidMessage) {
      setValue('merchantPaymentPlanId', null)
    } else if (!codePromo) {
      // Persist the plan that priced the selected term (a promo plan id, or the regular sentinel).
      // A typed promo code owns the plan selection instead, so leave it untouched in that flow.
      const selectedTerm = monthlyPaymentPerMonths.find((mp) => mp.term === getValues('term'))
      if (selectedTerm)
        setValue('merchantPaymentPlanId', selectedTerm.paymentPlanId ?? EPaymentPlan.RegularDailyInterests, {
          shouldValidate: true,
          shouldDirty: true,
        })
    }
    setNext()
  }

  if (isLoadingTerms) return <div className="loader" />

  return (
    <>
      {showPromoCodeInvalidMessage && (
        <h3 style={{ marginBottom: '16px' }}>
          <Trans
            i18nKey="worksheet.term.promoCodeTermInvalid"
            values={{
              phoneNumber: '1-888-689-9876',
            }}
            components={{ a: <Link to="tel:18886899876" style={{ textWrap: 'nowrap' }} /> }}
          />
        </h3>
      )}
      <h3 style={{ marginBottom: '16px' }}>
        <Trans
          i18nKey="worksheet.term.title"
          values={{ finalAmount: formatCurrency(finalAmount) }}
          components={{ accent: <span className="plan-selection__amount" /> }}
        />
      </h3>
      {plans.length > 0 && (
        <>
          <p>{t('worksheet.term.description')}</p>
          <p style={{ marginTop: '16px' }}>
            <Trans
              i18nKey="worksheet.plan.firstPaymentOn"
              values={{ date: format(new DateOnly(firstPaymentOn), 'PPP') }}
            />
          </p>
          <div className="plan-card-group" role="radiogroup" aria-label={t('worksheet.term.description')}>
            {plans.map((plan) => (
              <PlanCard
                key={plan.term}
                plan={plan}
                selected={plan.term === term}
                smallestPayment={plan.term === smallestPaymentTerm}
                onSelect={() => handleSelectPlan(plan)}
              />
            ))}
          </div>
          <div
            className="btn-group mobile-reversed full-width-mobile"
            style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '32px' }}
          >
            <button className="btn secondary-action-btn btn-small" onClick={setPrevious}>
              {t('worksheet.plan.backToFirstPayment')}
            </button>
            <button className="btn btn-blue btn-small" disabled={!selectedPlan} onClick={handleConfirmPlan}>
              {t('worksheet.plan.select')}
            </button>
          </div>
        </>
      )}
      {!plans.length && (
        <div className="paragraph">
          <p style={{ textAlign: 'center' }}>
            <Trans
              i18nKey="worksheet.term.empty"
              values={{
                phoneNumber: '1-888-689-9876',
              }}
              components={{ a: <Link to="tel:18886899876" style={{ textWrap: 'nowrap' }} /> }}
            />
          </p>
        </div>
      )}
    </>
  )
}

export default PlanSelectionStep
